import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import SearchableCombobox from "@/components/SearchableCombobox";
import { FileXls, FilePdf, FileCsv, GearSix } from "@phosphor-icons/react";

export const LEVELS = [["company", "Gesamtes Unternehmen"], ["property", "Objekt"], ["owner", "Eigentümer"], ["tenancy", "Mietverhältnis"]];
const PARAM = { property: "property_id", owner: "owner_contact_id", tenancy: "tenancy_id" };

export const levelParams = (level, entity) => (level !== "company" && entity ? { [PARAM[level]]: entity } : {});

export function LevelPicker({ level, setLevel, entity, setEntity }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    setOpts([]);
    if (level === "property") api.get("/properties").then(({ data }) => setOpts((data || []).map((p) => ({ id: p.id, label: `${p.property_number || ""} ${p.name}`.trim() }))));
    if (level === "owner") api.get("/contacts", { params: { role: "owner", limit: 500 } }).then(({ data }) => setOpts((data || []).map((c) => ({ id: c.id, label: c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() }))));
    if (level === "tenancy") api.get("/tenancies").then(({ data }) => setOpts((data?.items || data || []).map((t) => ({ id: t.id, label: `${t.contract_number} · ${(t.tenant_names || []).join(", ")}` }))));
  }, [level]);
  return (
    <>
      <div><Label>Ebene</Label>
        <select className="w-full h-9 border rounded-md px-2 text-sm bg-white" value={level} onChange={(e) => { setLevel(e.target.value); setEntity(""); }} data-testid="tax-level">
          {LEVELS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select></div>
      {level !== "company" && <div><Label>{LEVELS.find((l) => l[0] === level)[1]}</Label>
        <SearchableCombobox value={entity} onChange={setEntity} options={opts} placeholder="Auswählen…" testId="tax-entity" /></div>}
    </>
  );
}

function DatevSettings({ open, onOpenChange }) {
  const [f, setF] = useState({});
  useEffect(() => { if (open) api.get("/reports/tax-export/datev-settings").then(({ data }) => setF(data)); }, [open]);
  const save = async () => { await api.put("/reports/tax-export/datev-settings", { ...f, account_length: Number(f.account_length) || 4 }); toast.success("DATEV-Einstellungen gespeichert"); onOpenChange(false); };
  const F = (k, l) => <div key={k}><Label className="text-xs">{l}</Label><Input value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`datev-${k}`} /></div>;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="datev-settings-dialog">
        <DialogHeader><DialogTitle>DATEV-Kontenzuordnung</DialogTitle><DialogDescription>Vorbelegt mit SKR03-Beispielkonten – bitte mit Ihrem Steuerberater abstimmen.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {F("consultant_no", "Beraternummer")}{F("client_no", "Mandantennummer")}{F("bank_account", "Bankkonto (Soll)")}{F("account_length", "Sachkontenlänge")}
          {F("rent_cold", "Erlöskonto Kaltmiete")}{F("utilities", "Erlöskonto Betriebskosten-VZ")}{F("heating", "Erlöskonto Heizkosten-VZ")}{F("parking", "Erlöskonto Stellplatz")}{F("other", "Erlöskonto Sonstiges")}
        </div>
        <DialogFooter><Button onClick={save} data-testid="datev-save">Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TaxExportButtons({ from, to, level, entity }) {
  const [cfg, setCfg] = useState(false);
  const dl = async (format) => {
    if (level !== "company" && !entity) { toast.error("Bitte zuerst auswählen, wofür exportiert werden soll"); return; }
    try {
      const res = await api.get("/reports/tax-export", { params: { date_from: from, date_to: to, format, ...levelParams(level, entity) }, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `Steuerberater.${format}`;
      const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = name; a.click();
    } catch { toast.error("Export fehlgeschlagen"); }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="tax-export-buttons">
      <Button variant="outline" className="gap-1.5" onClick={() => dl("xlsx")} data-testid="tax-export-xlsx"><FileXls size={15} /> Excel</Button>
      <Button variant="outline" className="gap-1.5" onClick={() => dl("pdf")} data-testid="tax-export-pdf"><FilePdf size={15} /> PDF</Button>
      <Button variant="outline" className="gap-1.5" onClick={() => dl("datev")} data-testid="tax-export-datev"><FileCsv size={15} /> DATEV</Button>
      <Button variant="ghost" size="icon" onClick={() => setCfg(true)} title="DATEV-Kontenzuordnung" data-testid="tax-datev-settings"><GearSix size={16} /></Button>
      <DatevSettings open={cfg} onOpenChange={setCfg} />
    </div>
  );
}
